import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    ArrowLeft, ArrowRight, Save, Upload, Plus, Trash2,
    CheckCircle, Calendar, Download, FileSpreadsheet,
    Eye, Users, BookOpen, CalendarDays, CalendarRange, Target, Check, X
} from 'lucide-react'
import { toast } from 'sonner'
import { TestCategory, QuestionDifficulty, getBatches, mockQuestionBank, Test } from '@/data/mentor-mock-data'

type Step = 'details' | 'schedule' | 'contributors' | 'questions' | 'review'

interface Question {
    id: string
    subject: 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology'
    chapter: string
    difficulty: QuestionDifficulty
    text: string
    options: string[]
    correctOption: number
    marks: number
    negativeMarks: number
    explanation?: string
    timeSuggestion?: number
    tags?: string[]
}

interface TestConfig {
    title: string
    category: TestCategory
    subjects: string[]
    chapters: string[]
    duration: number
    totalMarks: number
    negativeMarking: { correct: number; incorrect: number }
    // Schedule
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    lateEntryAllowed: boolean
    lateEntryWindow: number
    attemptsAllowed: number
    // Visibility
    visibilityType: 'batch' | 'course' | 'all'
    visibilityTargets: string[]
    instructions: string
}

interface MentorAssignment {
    subject: 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology'
    mentorId: string
    mentorName: string
    status: 'pending' | 'locked' | 'submitted' | 'approved'
}

const categoryConfig: Record<TestCategory, { label: string; icon: React.ElementType; description: string }> = {
    chapter: { label: 'Chapter-wise', icon: BookOpen, description: 'Test on specific chapters of a subject' },
    daily: { label: 'Daily Practice', icon: CalendarDays, description: 'Quick daily practice tests' },
    weekly: { label: 'Weekly Test', icon: CalendarRange, description: 'Comprehensive weekly assessment' },
    monthly: { label: 'Monthly Series', icon: Calendar, description: 'Monthly comprehensive tests' },
    full: { label: 'Full Mock', icon: Target, description: 'Complete NEET pattern test' }
}

const chapters: Record<string, string[]> = {
    Physics: ['Mechanics', 'Thermodynamics', 'Optics', 'Electrostatics', 'Magnetism', 'Modern Physics', 'Waves'],
    Chemistry: ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Electrochemistry', 'Chemical Bonding'],
    Biology: ['Human Physiology', 'Cell Biology', 'Genetics', 'Ecology', 'Plant Physiology', 'Biotechnology'],
    Botany: ['Plant Physiology', 'Plant Taxonomy', 'Morphology of Flowering Plants', 'Anatomy of Flowering Plants', 'Plant Kingdom', 'Cell Biology'],
    Zoology: ['Human Physiology', 'Animal Diversity', 'Structural Organization in Animals', 'Ecology', 'Evolution', 'Genetics']
}

const mentors = [
    { id: 'mentor-001', name: 'Dr. Sarah', subject: 'Physics' },
    { id: 'mentor-002', name: 'Prof. Kumar', subject: 'Chemistry' },
    { id: 'mentor-004', name: 'Dr. Meera', subject: 'Botany' },
    { id: 'mentor-005', name: 'Dr. Anil', subject: 'Zoology' }
]

const courseOptions = [
    'Crash Course',
    'Long Term',
    'Short Term',
    '2-Year Integrated',
    'Dropper'
]

export function CreateTestView({ onCancel, existingTest }: { onCancel: () => void; existingTest?: Test }) {
    const batches = getBatches()
    const [currentStep, setCurrentStep] = useState<Step>(existingTest ? 'questions' : 'details')
    const [testConfig, setTestConfig] = useState<TestConfig>({
        title: existingTest?.title || '',
        category: existingTest?.category || 'chapter',
        subjects: existingTest?.sections?.map(s => s.subject) || ['Physics'],
        chapters: [],
        duration: existingTest?.duration || 60,
        totalMarks: existingTest?.totalMarks || 180,
        negativeMarking: { correct: 4, incorrect: 1 },
        startDate: '',
        startTime: '10:00',
        endDate: '',
        endTime: '11:00',
        lateEntryAllowed: true,
        lateEntryWindow: 10,
        attemptsAllowed: 1,
        visibilityType: existingTest?.visibility?.type || 'batch',
        visibilityTargets: existingTest?.visibility?.targets || [],
        instructions: '1. All questions are compulsory.\n2. +4 for correct answer, -1 for incorrect answer.\n3. No marks will be deducted for unattempted questions.'
    })
    const [questions, setQuestions] = useState<Question[]>([])
    const [mentorAssignments, setMentorAssignments] = useState<MentorAssignment[]>(
        existingTest?.sections?.map(section => ({
            subject: section.subject as 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology',
            mentorId: '',
            mentorName: 'Unassigned',
            status: 'pending' as const
        })) || []
    )
    const [activeTab, setActiveTab] = useState<'manual' | 'bulk' | 'bank'>('manual')
    const [selectedQuestionBank, setSelectedQuestionBank] = useState<string[]>([])

    const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
        subject: 'Physics',
        chapter: '',
        difficulty: 'medium',
        text: '',
        options: ['', '', '', ''],
        correctOption: 0,
        marks: 4,
        negativeMarks: 1
    })

    // Custom chapters state
    const [customChapters, setCustomChapters] = useState<Record<string, string[]>>({})
    const [newChapterInput, setNewChapterInput] = useState('')
    const [addingChapterFor, setAddingChapterFor] = useState<string | null>(null)

    // Preview modal state
    // const [showPreview, setShowPreview] = useState(false)

    // Determine if test needs collaborative section
    const needsCollaboration = ['weekly', 'monthly', 'full'].includes(testConfig.category) && testConfig.subjects.length > 1

    const getSteps = (): { id: Step; label: string }[] => {
        const baseSteps: { id: Step; label: string }[] = [
            { id: 'details', label: 'Test Details' },
            { id: 'schedule', label: 'Schedule & Access' }
        ]
        if (needsCollaboration) {
            baseSteps.push({ id: 'contributors', label: 'Contributors' })
        }
        baseSteps.push({ id: 'questions', label: 'Questions' })
        baseSteps.push({ id: 'review', label: 'Review' })
        return baseSteps
    }

    const steps = getSteps()

    const handleNext = () => {
        const currentIndex = steps.findIndex(s => s.id === currentStep)
        if (currentIndex < steps.length - 1) {
            setCurrentStep(steps[currentIndex + 1].id)
        }
    }

    const handleBack = () => {
        const currentIndex = steps.findIndex(s => s.id === currentStep)
        if (currentIndex > 0) {
            setCurrentStep(steps[currentIndex - 1].id)
        }
    }

    const handleSubjectToggle = (subject: string) => {
        const newSubjects = testConfig.subjects.includes(subject)
            ? testConfig.subjects.filter(s => s !== subject)
            : [...testConfig.subjects, subject]
        setTestConfig({ ...testConfig, subjects: newSubjects, chapters: [] })

        // Update mentor assignments if needed
        if (needsCollaboration) {
            const newAssignments = newSubjects.map(sub => {
                const existing = mentorAssignments.find(a => a.subject === sub)
                if (existing) return existing
                const defaultMentor = mentors.find(m => m.subject === sub)
                return {
                    subject: sub as 'Physics' | 'Chemistry' | 'Biology',
                    mentorId: defaultMentor?.id || '',
                    mentorName: defaultMentor?.name || 'Unassigned',
                    status: 'pending' as const
                }
            })
            setMentorAssignments(newAssignments)
        }
    }

    const handleChapterToggle = (chapter: string) => {
        const newChapters = testConfig.chapters.includes(chapter)
            ? testConfig.chapters.filter(c => c !== chapter)
            : [...testConfig.chapters, chapter]
        setTestConfig({ ...testConfig, chapters: newChapters })
    }

    const handleAddCustomChapter = (subject: string) => {
        if (!newChapterInput.trim()) return
        setCustomChapters(prev => ({
            ...prev,
            [subject]: [...(prev[subject] || []), newChapterInput.trim()]
        }))
        setNewChapterInput('')
        setAddingChapterFor(null)
    }

    // Get all chapters including custom ones
    const getAllChapters = (subject: string) => {
        return [...(chapters[subject] || []), ...(customChapters[subject] || [])]
    }

    const handleAddQuestion = () => {
        if (!newQuestion.text || !newQuestion.subject || newQuestion.options?.some(o => !o)) {
            toast.error('Please fill all question fields')
            return
        }

        const q: Question = {
            id: Date.now().toString(),
            subject: newQuestion.subject as 'Physics' | 'Chemistry' | 'Biology',
            chapter: newQuestion.chapter || '',
            difficulty: newQuestion.difficulty || 'medium',
            text: newQuestion.text,
            options: newQuestion.options as string[],
            correctOption: newQuestion.correctOption || 0,
            marks: newQuestion.marks || 4,
            negativeMarks: newQuestion.negativeMarks || 1,
            explanation: newQuestion.explanation,
            timeSuggestion: newQuestion.timeSuggestion
        }

        setQuestions([...questions, q])
        setNewQuestion({
            subject: newQuestion.subject,
            chapter: '',
            difficulty: 'medium',
            text: '',
            options: ['', '', '', ''],
            correctOption: 0,
            marks: 4,
            negativeMarks: 1
        })
        toast.success('Question added')
    }

    const handleImportFromBank = () => {
        const selected = mockQuestionBank.filter(q => selectedQuestionBank.includes(q.id))
        const newQuestions: Question[] = selected.map(q => ({
            id: `imported-${q.id}`,
            subject: q.subject,
            chapter: q.chapter,
            difficulty: q.difficulty,
            text: q.text,
            options: q.options,
            correctOption: q.correctOption,
            marks: 4,
            negativeMarks: 1,
            explanation: q.explanation,
            timeSuggestion: q.timeSuggestion,
            tags: q.tags
        }))
        setQuestions([...questions, ...newQuestions])
        setSelectedQuestionBank([])
        toast.success(`${newQuestions.length} questions imported`)
    }

    const downloadSampleTemplate = () => {
        toast.success('Downloading sample Excel template...')
    }

    const filteredQuestionBank = mockQuestionBank.filter(q =>
        testConfig.subjects.includes(q.subject) &&
        !questions.some(existing => existing.id === `imported-${q.id}`)
    )

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div>
                <button
                    onClick={currentStep === 'details' ? onCancel : handleBack}
                    className="flex items-center gap-2 text-theme-secondary hover:text-theme-primary mb-3 transition-colors text-sm"
                >
                    <ArrowLeft className="h-4 w-4" />
                    {currentStep === 'details' || existingTest ? 'Back to Test List' : 'Back'}
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-theme-primary">
                            {existingTest ? `Edit Test: ${existingTest.title}` : 'Create New Test'}
                        </h2>
                        <p className="text-sm text-theme-secondary">
                            {existingTest ? 'Add questions to your assigned sections' : 'Configure test details, questions, and rules'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="px-3 py-2 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors font-medium text-sm">
                            <Save className="h-4 w-4 inline mr-1" />
                            Save Draft
                        </button>
                        {!existingTest && currentStep !== steps[steps.length - 1].id && (
                            <button
                                onClick={handleNext}
                                className="px-4 py-2 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/25 text-sm flex items-center gap-1"
                            >
                                Next <ArrowRight className="h-4 w-4" />
                            </button>
                        )}
                        {existingTest && (
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/25 text-sm flex items-center gap-1"
                            >
                                Done <Check className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Progress Steps - Responsive (Only show if NOT editing) */}
            {!existingTest && (
                <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2">
                    <div className="flex items-center justify-between min-w-max md:min-w-0 relative gap-2">
                        <div className="absolute left-4 right-4 top-4 h-0.5 bg-slate-200 dark:bg-slate-700 -z-10 hidden md:block" />
                        {steps.map((step, index) => {
                            const isCompleted = steps.findIndex(s => s.id === currentStep) > index
                            const isCurrent = step.id === currentStep
                            return (
                                <button
                                    key={step.id}
                                    onClick={() => setCurrentStep(step.id)}
                                    className="flex flex-col items-center gap-1 md:gap-2 bg-slate-50 dark:bg-slate-900 px-2 md:px-4"
                                >
                                    <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm transition-all ${isCompleted ? 'bg-green-500 text-white' :
                                        isCurrent ? 'bg-primary-blue text-white ring-2 md:ring-4 ring-blue-100 dark:ring-blue-900/30' :
                                            'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                        }`}>
                                        {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                                    </div>
                                    <span className={`text-[10px] md:text-xs font-medium whitespace-nowrap ${isCurrent ? 'text-primary-blue' : 'text-theme-secondary'
                                        }`}>{step.label}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Step Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                >
                    {/* STEP 1: Test Details */}
                    {currentStep === 'details' && (
                        <div className="space-y-4 md:space-y-6">
                            {/* Category Selection */}
                            <GlassmorphicCard className="p-4 md:p-6">
                                <h3 className="font-bold text-theme-primary mb-4">Test Category</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                    {(Object.keys(categoryConfig) as TestCategory[]).map(cat => {
                                        const config = categoryConfig[cat]
                                        const Icon = config.icon
                                        return (
                                            <button
                                                key={cat}
                                                onClick={() => setTestConfig({ ...testConfig, category: cat })}
                                                className={`p-3 md:p-4 rounded-xl border-2 transition-all text-left ${testConfig.category === cat
                                                    ? 'border-primary-blue bg-primary-blue/5'
                                                    : 'border-slate-200 dark:border-slate-700 hover:border-primary-blue/50'
                                                    }`}
                                            >
                                                <Icon className={`h-5 w-5 mb-2 ${testConfig.category === cat ? 'text-primary-blue' : 'text-theme-secondary'}`} />
                                                <p className="font-semibold text-sm text-theme-primary">{config.label}</p>
                                                <p className="text-xs text-theme-secondary mt-1 line-clamp-2">{config.description}</p>
                                            </button>
                                        )
                                    })}
                                </div>
                            </GlassmorphicCard>

                            {/* Basic Details */}
                            <GlassmorphicCard className="p-4 md:p-6 space-y-4 md:space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Test Title</label>
                                        <input
                                            type="text"
                                            value={testConfig.title}
                                            onChange={e => setTestConfig({ ...testConfig, title: e.target.value })}
                                            placeholder="e.g. Physics Major Test 1"
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>

                                    {/* Subject Selection (Biology split into Botany & Zoology) */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-theme-secondary mb-2">Subjects</label>
                                        <div className="flex flex-wrap gap-2">
                                            {['Physics', 'Chemistry', 'Biology', 'Botany', 'Zoology'].map(subject => (
                                                <button
                                                    key={subject}
                                                    onClick={() => handleSubjectToggle(subject)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${testConfig.subjects.includes(subject)
                                                        ? 'bg-primary-blue text-white'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                                        }`}
                                                >
                                                    {testConfig.subjects.includes(subject) && <Check className="h-4 w-4" />}
                                                    {subject}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Chapter Selection (for chapter-wise tests) */}
                                    {testConfig.category === 'chapter' && (
                                        <div className="md:col-span-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-sm font-medium text-theme-secondary">Chapters</label>
                                            </div>
                                            {testConfig.subjects.map(subject => (
                                                <div key={subject} className="mb-3">
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <p className="text-xs text-theme-secondary">{subject}</p>

                                                        {addingChapterFor === subject ? (
                                                            <div className="flex items-center gap-1 animate-in fade-in slide-in-from-right-4 duration-200">
                                                                <input
                                                                    type="text"
                                                                    value={newChapterInput}
                                                                    onChange={e => setNewChapterInput(e.target.value)}
                                                                    placeholder="Chapter Name"
                                                                    className="px-2 py-0.5 text-xs border border-primary-blue rounded bg-white dark:bg-slate-700 text-theme-primary outline-none w-32"
                                                                    autoFocus
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') handleAddCustomChapter(subject)
                                                                        if (e.key === 'Escape') setAddingChapterFor(null)
                                                                    }}
                                                                />
                                                                <button
                                                                    onClick={() => handleAddCustomChapter(subject)}
                                                                    className="p-0.5 rounded bg-green-100 text-green-600 hover:bg-green-200"
                                                                >
                                                                    <Check className="h-3 w-3" />
                                                                </button>
                                                                <button
                                                                    onClick={() => setAddingChapterFor(null)}
                                                                    className="p-0.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setAddingChapterFor(subject)}
                                                                className="text-[10px] font-medium text-primary-blue hover:underline flex items-center gap-0.5"
                                                            >
                                                                <Plus className="h-3 w-3" /> Add Chapter
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl min-h-[50px]">
                                                        {getAllChapters(subject).length === 0 ? (
                                                            <span className="text-xs text-slate-400 italic">No chapters available</span>
                                                        ) : (
                                                            getAllChapters(subject).map(chapter => (
                                                                <button
                                                                    key={chapter}
                                                                    onClick={() => handleChapterToggle(chapter)}
                                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${testConfig.chapters.includes(chapter)
                                                                        ? 'bg-primary-blue text-white shadow-sm shadow-blue-500/30'
                                                                        : 'bg-white dark:bg-slate-700 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
                                                                        }`}
                                                                >
                                                                    {chapter}
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Duration (minutes)</label>
                                        <input
                                            type="number"
                                            value={testConfig.duration}
                                            onChange={e => setTestConfig({ ...testConfig, duration: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Total Marks</label>
                                        <input
                                            type="number"
                                            value={testConfig.totalMarks}
                                            onChange={e => setTestConfig({ ...testConfig, totalMarks: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Marks per Correct</label>
                                        <input
                                            type="number"
                                            value={testConfig.negativeMarking.correct}
                                            onChange={e => setTestConfig({
                                                ...testConfig,
                                                negativeMarking: { ...testConfig.negativeMarking, correct: parseInt(e.target.value) || 0 }
                                            })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Negative Marks</label>
                                        <input
                                            type="number"
                                            value={testConfig.negativeMarking.incorrect}
                                            onChange={e => setTestConfig({
                                                ...testConfig,
                                                negativeMarking: { ...testConfig.negativeMarking, incorrect: parseInt(e.target.value) || 0 }
                                            })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </div>
                    )}

                    {/* STEP 2: Schedule & Access */}
                    {currentStep === 'schedule' && (
                        <div className="space-y-4 md:space-y-6">
                            <GlassmorphicCard className="p-4 md:p-6 space-y-6">
                                <h3 className="font-bold text-theme-primary flex items-center gap-2">
                                    <Calendar className="h-5 w-5 text-primary-blue" />
                                    Schedule
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            value={testConfig.startDate}
                                            onChange={e => setTestConfig({ ...testConfig, startDate: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Start Time</label>
                                        <input
                                            type="time"
                                            value={testConfig.startTime}
                                            onChange={e => setTestConfig({ ...testConfig, startTime: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">End Date</label>
                                        <input
                                            type="date"
                                            value={testConfig.endDate}
                                            onChange={e => setTestConfig({ ...testConfig, endDate: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">End Time</label>
                                        <input
                                            type="time"
                                            value={testConfig.endTime}
                                            onChange={e => setTestConfig({ ...testConfig, endTime: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        />
                                    </div>
                                </div>

                                {/* Late Entry & Attempts */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                        <div>
                                            <p className="font-medium text-theme-primary">Late Entry</p>
                                            <p className="text-xs text-theme-secondary">Allow students to join late</p>
                                        </div>
                                        <button
                                            onClick={() => setTestConfig({ ...testConfig, lateEntryAllowed: !testConfig.lateEntryAllowed })}
                                            className={`w-12 h-6 rounded-full transition-colors ${testConfig.lateEntryAllowed ? 'bg-primary-blue' : 'bg-slate-300 dark:bg-slate-600'
                                                }`}
                                        >
                                            <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${testConfig.lateEntryAllowed ? 'translate-x-6' : 'translate-x-0.5'
                                                }`} />
                                        </button>
                                    </div>
                                    {testConfig.lateEntryAllowed && (
                                        <div>
                                            <label className="block text-sm font-medium text-theme-secondary mb-1">Late Entry Window (min)</label>
                                            <input
                                                type="number"
                                                value={testConfig.lateEntryWindow}
                                                onChange={e => setTestConfig({ ...testConfig, lateEntryWindow: parseInt(e.target.value) || 0 })}
                                                className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Attempts Allowed</label>
                                        <select
                                            value={testConfig.attemptsAllowed}
                                            onChange={e => setTestConfig({ ...testConfig, attemptsAllowed: parseInt(e.target.value) })}
                                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary"
                                        >
                                            <option value={1}>1 Attempt</option>
                                            <option value={2}>2 Attempts</option>
                                            <option value={3}>3 Attempts</option>
                                            <option value={99}>Unlimited</option>
                                        </select>
                                    </div>
                                </div>
                            </GlassmorphicCard>

                            {/* Visibility */}
                            <GlassmorphicCard className="p-4 md:p-6 space-y-4">
                                <h3 className="font-bold text-theme-primary flex items-center gap-2">
                                    <Users className="h-5 w-5 text-primary-blue" />
                                    Visibility
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {(['batch', 'course', 'all'] as const).map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setTestConfig({ ...testConfig, visibilityType: type, visibilityTargets: [] })}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${testConfig.visibilityType === type
                                                ? 'bg-primary-blue text-white'
                                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200'
                                                }`}
                                        >
                                            {type === 'batch' ? 'Batch-specific' : type === 'course' ? 'Course-specific' : 'All Paid Users'}
                                        </button>
                                    ))}
                                </div>
                                {testConfig.visibilityType === 'batch' && (
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-2">Select Batches</label>
                                        <div className="flex flex-wrap gap-2">
                                            {batches.map(batch => (
                                                <button
                                                    key={batch}
                                                    onClick={() => {
                                                        const newTargets = testConfig.visibilityTargets.includes(batch)
                                                            ? testConfig.visibilityTargets.filter(t => t !== batch)
                                                            : [...testConfig.visibilityTargets, batch]
                                                        setTestConfig({ ...testConfig, visibilityTargets: newTargets })
                                                    }}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${testConfig.visibilityTargets.includes(batch)
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200'
                                                        }`}
                                                >
                                                    {testConfig.visibilityTargets.includes(batch) && <Check className="h-3 w-3" />}
                                                    {batch}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {testConfig.visibilityType === 'course' && (
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-2">Select Courses</label>
                                        <div className="flex flex-wrap gap-2">
                                            {courseOptions.map(course => (
                                                <button
                                                    key={course}
                                                    onClick={() => {
                                                        const newTargets = testConfig.visibilityTargets.includes(course)
                                                            ? testConfig.visibilityTargets.filter(t => t !== course)
                                                            : [...testConfig.visibilityTargets, course]
                                                        setTestConfig({ ...testConfig, visibilityTargets: newTargets })
                                                    }}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${testConfig.visibilityTargets.includes(course)
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200'
                                                        }`}
                                                >
                                                    {testConfig.visibilityTargets.includes(course) && <Check className="h-3 w-3" />}
                                                    {course}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </GlassmorphicCard>
                        </div>
                    )}

                    {/* STEP 3: Contributors (for collaborative tests) */}
                    {currentStep === 'contributors' && needsCollaboration && (
                        <GlassmorphicCard className="p-4 md:p-6 space-y-6">
                            <div className="flex items-center gap-2">
                                <Users className="h-5 w-5 text-primary-blue" />
                                <h3 className="font-bold text-theme-primary">Assign Section Contributors</h3>
                            </div>
                            <p className="text-sm text-theme-secondary">Assign mentors to create questions for each subject section.</p>

                            <div className="space-y-3">
                                {testConfig.subjects.map(subject => {
                                    // If Biology is selected, split into Botany and Zoology
                                    if (subject === 'Biology') {
                                        return (
                                            <div key={subject} className="space-y-3">
                                                {/* Botany Section */}
                                                {(() => {
                                                    const assignment = mentorAssignments.find(a => a.subject === 'Botany')
                                                    return (
                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100 text-green-600">
                                                                    <BookOpen className="h-5 w-5" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-theme-primary">Botany Section</p>
                                                                    <p className="text-xs text-theme-secondary">45 questions required</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <select
                                                                    value={assignment?.mentorId || ''}
                                                                    onChange={e => {
                                                                        const mentor = mentors.find(m => m.id === e.target.value)
                                                                        setMentorAssignments(prev => {
                                                                            const exists = prev.find(a => a.subject === 'Botany')
                                                                            if (exists) {
                                                                                return prev.map(a => a.subject === 'Botany'
                                                                                    ? { ...a, mentorId: e.target.value, mentorName: mentor?.name || 'Unassigned' }
                                                                                    : a
                                                                                )
                                                                            } else {
                                                                                return [...prev, {
                                                                                    subject: 'Botany' as const,
                                                                                    mentorId: e.target.value,
                                                                                    mentorName: mentor?.name || 'Unassigned',
                                                                                    status: 'pending' as const
                                                                                }]
                                                                            }
                                                                        })
                                                                    }}
                                                                    className="px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-sm outline-none"
                                                                >
                                                                    <option value="">Select Mentor</option>
                                                                    {mentors.filter(m => m.subject === 'Botany').map(m => (
                                                                        <option key={m.id} value={m.id}>{m.name}</option>
                                                                    ))}
                                                                </select>
                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${assignment?.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                                    assignment?.status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                                                                        assignment?.status === 'locked' ? 'bg-blue-100 text-blue-700' :
                                                                            'bg-slate-100 text-slate-600'
                                                                    }`}>
                                                                    {assignment?.status || 'pending'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )
                                                })()}

                                                {/* Zoology Section */}
                                                {(() => {
                                                    const assignment = mentorAssignments.find(a => a.subject === 'Zoology')
                                                    return (
                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100 text-purple-600">
                                                                    <BookOpen className="h-5 w-5" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-theme-primary">Zoology Section</p>
                                                                    <p className="text-xs text-theme-secondary">45 questions required</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <select
                                                                    value={assignment?.mentorId || ''}
                                                                    onChange={e => {
                                                                        const mentor = mentors.find(m => m.id === e.target.value)
                                                                        setMentorAssignments(prev => {
                                                                            const exists = prev.find(a => a.subject === 'Zoology')
                                                                            if (exists) {
                                                                                return prev.map(a => a.subject === 'Zoology'
                                                                                    ? { ...a, mentorId: e.target.value, mentorName: mentor?.name || 'Unassigned' }
                                                                                    : a
                                                                                )
                                                                            } else {
                                                                                return [...prev, {
                                                                                    subject: 'Zoology' as const,
                                                                                    mentorId: e.target.value,
                                                                                    mentorName: mentor?.name || 'Unassigned',
                                                                                    status: 'pending' as const
                                                                                }]
                                                                            }
                                                                        })
                                                                    }}
                                                                    className="px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-sm outline-none"
                                                                >
                                                                    <option value="">Select Mentor</option>
                                                                    {mentors.filter(m => m.subject === 'Zoology').map(m => (
                                                                        <option key={m.id} value={m.id}>{m.name}</option>
                                                                    ))}
                                                                </select>
                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${assignment?.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                                    assignment?.status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                                                                        assignment?.status === 'locked' ? 'bg-blue-100 text-blue-700' :
                                                                            'bg-slate-100 text-slate-600'
                                                                    }`}>
                                                                    {assignment?.status || 'pending'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )
                                                })()}
                                            </div>
                                        )
                                    }

                                    // For Physics and Chemistry, show as before
                                    const assignment = mentorAssignments.find(a => a.subject === subject)
                                    return (
                                        <div key={subject} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${subject === 'Physics' ? 'bg-blue-100 text-blue-600' :
                                                    subject === 'Chemistry' ? 'bg-green-100 text-green-600' :
                                                        'bg-purple-100 text-purple-600'
                                                    }`}>
                                                    <BookOpen className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-theme-primary">{subject} Section</p>
                                                    <p className="text-xs text-theme-secondary">45 questions required</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <select
                                                    value={assignment?.mentorId || ''}
                                                    onChange={e => {
                                                        const mentor = mentors.find(m => m.id === e.target.value)
                                                        setMentorAssignments(prev =>
                                                            prev.map(a => a.subject === subject
                                                                ? { ...a, mentorId: e.target.value, mentorName: mentor?.name || 'Unassigned' }
                                                                : a
                                                            )
                                                        )
                                                    }}
                                                    className="px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-sm outline-none"
                                                >
                                                    <option value="">Select Mentor</option>
                                                    {mentors.filter(m => m.subject === subject).map(m => (
                                                        <option key={m.id} value={m.id}>{m.name}</option>
                                                    ))}
                                                </select>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${assignment?.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                    assignment?.status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                                                        assignment?.status === 'locked' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-slate-100 text-slate-600'
                                                    }`}>
                                                    {assignment?.status || 'pending'}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </GlassmorphicCard>
                    )}

                    {/* STEP 4: Questions */}
                    {currentStep === 'questions' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                            {/* Left: Input Methods */}
                            <div className="lg:col-span-2 space-y-4">
                                <GlassmorphicCard className="p-4 md:p-6">
                                    {/* Tabs */}
                                    <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-3 mb-4 overflow-x-auto">
                                        {(['manual', 'bulk', 'bank'] as const).map(tab => (
                                            <button
                                                key={tab}
                                                onClick={() => setActiveTab(tab)}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab
                                                    ? 'bg-primary-blue text-white'
                                                    : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                {tab === 'manual' ? 'Manual Entry' : tab === 'bulk' ? 'Bulk Upload' : 'Question Bank'}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Manual Entry */}
                                    {activeTab === 'manual' && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-theme-secondary mb-1">Question Text</label>
                                                <textarea
                                                    value={newQuestion.text}
                                                    onChange={e => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                                    placeholder="Type your question here..."
                                                    className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-theme-primary h-24 resize-none text-sm"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {newQuestion.options?.map((opt, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name="correctOption"
                                                            checked={newQuestion.correctOption === i}
                                                            onChange={() => setNewQuestion({ ...newQuestion, correctOption: i })}
                                                            className="w-4 h-4 text-primary-blue"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={opt}
                                                            onChange={e => {
                                                                const newOptions = [...(newQuestion.options || [])]
                                                                newOptions[i] = e.target.value
                                                                setNewQuestion({ ...newQuestion, options: newOptions })
                                                            }}
                                                            placeholder={`Option ${String.fromCharCode(65 + i)}`}
                                                            className="flex-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            <textarea
                                                value={newQuestion.explanation || ''}
                                                onChange={e => setNewQuestion({ ...newQuestion, explanation: e.target.value })}
                                                placeholder="Explanation (optional)"
                                                className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm h-16 resize-none"
                                            />
                                            <button
                                                onClick={handleAddQuestion}
                                                className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 text-theme-primary font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 text-sm"
                                            >
                                                <Plus className="h-4 w-4" /> Add Question
                                            </button>
                                        </div>
                                    )}

                                    {/* Bulk Upload */}
                                    {activeTab === 'bulk' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-semibold text-theme-primary flex items-center gap-2">
                                                    <FileSpreadsheet className="h-5 w-5 text-green-600" />
                                                    Bulk Upload via Excel
                                                </h4>
                                                <button
                                                    onClick={downloadSampleTemplate}
                                                    className="text-sm text-primary-blue hover:underline flex items-center gap-1"
                                                >
                                                    <Download className="h-4 w-4" /> Download Template
                                                </button>
                                            </div>
                                            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                                <p className="text-xs text-theme-secondary mb-2 font-medium">Expected Format:</p>
                                                <div className="text-xs text-theme-secondary">
                                                    Question | Option A | Option B | Option C | Option D | Correct | Marks
                                                </div>
                                            </div>
                                            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
                                                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                                                <p className="text-sm text-theme-secondary">Drag & drop or click to upload</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Question Bank */}
                                    {activeTab === 'bank' && (
                                        <div className="space-y-4">
                                            <p className="text-sm text-theme-secondary">Select questions from the question bank to import.</p>
                                            <div className="max-h-64 overflow-y-auto space-y-2">
                                                {filteredQuestionBank.length === 0 ? (
                                                    <p className="text-center text-theme-secondary py-4">No questions available for selected subjects</p>
                                                ) : (
                                                    filteredQuestionBank.map(q => (
                                                        <div
                                                            key={q.id}
                                                            onClick={() => {
                                                                setSelectedQuestionBank(prev =>
                                                                    prev.includes(q.id) ? prev.filter(id => id !== q.id) : [...prev, q.id]
                                                                )
                                                            }}
                                                            className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedQuestionBank.includes(q.id)
                                                                ? 'border-primary-blue bg-primary-blue/5'
                                                                : 'border-slate-200 dark:border-slate-700 hover:border-primary-blue/50'
                                                                }`}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${selectedQuestionBank.includes(q.id)
                                                                    ? 'border-primary-blue bg-primary-blue'
                                                                    : 'border-slate-300 dark:border-slate-600'
                                                                    }`}>
                                                                    {selectedQuestionBank.includes(q.id) && <Check className="h-3 w-3 text-white" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm text-theme-primary line-clamp-2">{q.text}</p>
                                                                    <div className="flex gap-2 mt-1">
                                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-theme-secondary">{q.subject}</span>
                                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-theme-secondary">{q.chapter}</span>
                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                                                            q.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                                                                'bg-amber-100 text-amber-700'
                                                                            }`}>{q.difficulty}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            {selectedQuestionBank.length > 0 && (
                                                <button
                                                    onClick={handleImportFromBank}
                                                    className="w-full py-2.5 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
                                                >
                                                    Import {selectedQuestionBank.length} Questions
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </GlassmorphicCard>
                            </div>

                            {/* Right: Question Summary */}
                            <div className="space-y-4">
                                <GlassmorphicCard className="p-4 bg-primary-blue/5 border-primary-blue/20">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-theme-secondary">Total Questions</span>
                                        <span className="text-xl font-bold text-primary-blue">{questions.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-theme-secondary">Total Marks</span>
                                        <span className="text-xl font-bold text-primary-blue">{questions.reduce((sum, q) => sum + q.marks, 0)}</span>
                                    </div>
                                </GlassmorphicCard>

                                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                    {questions.map((q, idx) => (
                                        <GlassmorphicCard key={q.id} className="p-3 group hover:border-primary-blue/30 transition-colors">
                                            <div className="flex justify-between items-start">
                                                <p className="text-sm font-medium text-theme-primary line-clamp-2 flex-1">
                                                    <span className="text-theme-secondary mr-2">{idx + 1}.</span>
                                                    {q.text}
                                                </p>
                                                <button
                                                    onClick={() => setQuestions(questions.filter(qn => qn.id !== q.id))}
                                                    className="text-red-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                            <div className="flex gap-1.5 mt-2 flex-wrap">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${q.subject === 'Physics' ? 'bg-blue-100 text-blue-700' :
                                                    q.subject === 'Chemistry' ? 'bg-green-100 text-green-700' :
                                                        'bg-purple-100 text-purple-700'
                                                    }`}>{q.subject}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                                    q.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                    }`}>{q.difficulty}</span>
                                            </div>
                                        </GlassmorphicCard>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 5: Review */}
                    {currentStep === 'review' && (
                        <div className="space-y-4 md:space-y-6">
                            <GlassmorphicCard className="p-4 md:p-6">
                                <h3 className="font-bold text-theme-primary text-lg mb-4">Review Test Details</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                                    <div>
                                        <p className="text-xs text-theme-secondary">Test Title</p>
                                        <p className="font-semibold text-theme-primary">{testConfig.title || 'Untitled Test'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Category</p>
                                        <p className="font-semibold text-theme-primary capitalize">{testConfig.category}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Subjects</p>
                                        <p className="font-semibold text-theme-primary">{testConfig.subjects.join(', ')}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Duration</p>
                                        <p className="font-semibold text-theme-primary">{testConfig.duration} min</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Total Marks</p>
                                        <p className="font-semibold text-theme-primary">{testConfig.totalMarks}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Questions</p>
                                        <p className="font-semibold text-theme-primary">{questions.length}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Visibility</p>
                                        <p className="font-semibold text-theme-primary capitalize">{testConfig.visibilityType}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-theme-secondary">Attempts</p>
                                        <p className="font-semibold text-theme-primary">{testConfig.attemptsAllowed === 99 ? 'Unlimited' : testConfig.attemptsAllowed}</p>
                                    </div>
                                </div>
                            </GlassmorphicCard>

                            {questions.length > 0 && (
                                <GlassmorphicCard className="p-4 md:p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-bold text-theme-primary text-lg">Questions Preview ({questions.length})</h3>
                                        <button
                                            onClick={() => setCurrentStep('questions')}
                                            className="text-primary-blue hover:underline text-sm font-medium"
                                        >
                                            Edit Questions
                                        </button>
                                    </div>
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                        {questions.slice(0, 5).map((q, idx) => (
                                            <div key={q.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                                <p className="text-sm text-theme-primary">
                                                    <span className="text-theme-secondary font-mono mr-2">{idx + 1}.</span>
                                                    {q.text}
                                                </p>
                                            </div>
                                        ))}
                                        {questions.length > 5 && (
                                            <p className="text-center text-sm text-theme-secondary">...and {questions.length - 5} more questions</p>
                                        )}
                                    </div>
                                </GlassmorphicCard>
                            )}

                            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                                <button className="px-6 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors font-semibold">
                                    Save as Draft
                                </button>
                                <button
                                    onClick={() => {
                                        // setShowPreview(true)
                                        toast.info('Preview mode: Showing test as student would see it')
                                    }}
                                    className="px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-theme-primary hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors font-semibold flex items-center justify-center gap-2"
                                >
                                    <Eye className="h-4 w-4" /> Preview as Student
                                </button>
                                <button className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-primary-blue to-blue-600 text-white font-bold hover:shadow-lg hover:shadow-blue-500/25 transition-all">
                                    🚀 Publish Test
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    )
}
